// Matching rules for the Price Lists page filters (specs/price-lists.md).
// Kept out of the view so each rule is unit-testable on its own.

export interface PriceListPartner {
  name?: string | null;
}

export interface PriceListPartNumberEntry {
  partNumber?: string | null;
}

export interface FilterablePriceList {
  partners?: PriceListPartner[] | null;
  priceRanges?: PriceListPartNumberEntry[] | null;
  priceStatics?: PriceListPartNumberEntry[] | null;
  trustedSeries?: PriceListPartNumberEntry[] | null;
  endDate?: string | null;
}

export interface PriceListFilters {
  partner?: string;
  partNumber?: string;
  showExpired?: boolean;
}

/**
 * Every partner used by at least one price list: trimmed, de-duplicated and
 * alphabetised, for the partner filter's options. Trimming matters — the data
 * holds at least one partner stored with a leading tab, which would otherwise
 * show up as a second, identical-looking option.
 */
export const usedPartnerNames = (priceLists: FilterablePriceList[] = []): string[] => {
  const names = priceLists.flatMap((priceList) =>
    (priceList.partners || []).map((partner) => (partner?.name || '').trim()).filter(Boolean),
  );
  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
};

const includesPartNumber = (entries: PriceListPartNumberEntry[] | null | undefined, needle: string): boolean =>
  Array.isArray(entries) && entries.some((entry) => (entry?.partNumber || '').toLowerCase().includes(needle));

/**
 * Today at UTC midnight — the cutoff a price list's `endDate` is compared
 * against. The boundaries are stored as Chicago end-of-day expressed in UTC,
 * so comparing against the UTC date keeps a list visible for the whole of its
 * final day rather than dropping it that morning.
 */
export const expiryCutoff = (now: Date = new Date()): Date =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

/**
 * Applies the three filters together. A price list survives when it matches
 * the selected partner exactly, contains the part-number substring in any of
 * its ranges, statics or trusted series, and has not expired (unless expired
 * lists are being shown).
 */
export const filterPriceLists = (
  priceLists: FilterablePriceList[] = [],
  { partner = '', partNumber = '', showExpired = false }: PriceListFilters = {},
  now: Date = new Date(),
): FilterablePriceList[] => {
  const cutoff = expiryCutoff(now);
  const needle = partNumber.trim().toLowerCase();

  return priceLists.filter((priceList) => {
    const matchesPartner =
      !partner || (priceList.partners || []).some((entry) => (entry?.name || '').trim() === partner);

    const matchesPartNumber =
      !needle ||
      includesPartNumber(priceList.priceRanges, needle) ||
      includesPartNumber(priceList.priceStatics, needle) ||
      includesPartNumber(priceList.trustedSeries, needle);

    // A list with no end date can't be proven unexpired, so it only shows
    // when expired lists are included.
    const matchesExpiration = showExpired || (!!priceList.endDate && new Date(priceList.endDate) >= cutoff);

    return matchesPartner && matchesPartNumber && matchesExpiration;
  });
};
